/**
 * MemoryEventFeed — debug overlay showing real-time memory activity.
 *
 * Renders a stack of slim toasts in the bottom-right corner (above the badge).
 * Each toast auto-dismisses after 60 seconds but also has an X button.
 * A persistent log drawer can be opened for full session history.
 */

import { useEffect, useRef, useState, useCallback } from "react";
import type { MemoryEvent } from "@/hooks/useNotifications";
import styles from "./MemoryEventFeed.module.css";

const AUTO_DISMISS_MS = 60_000;
const MAX_VISIBLE = 4;

// Per event-type: color for left border + dot, and a short tag label.
// Tags are user-facing — keep them honest about whether the row is
// already a first-class memory (saved/active) or still a candidate
// awaiting your confirmation in /admin/knowledge.
const EVENT_META: Record<string, { color: string; tag: string }> = {
  // Path A (explicit save_fact) — fully saved, no review needed.
  fact_saved:                  { color: "#315c2b", tag: "Saved" },
  fact_pending_confirmation:   { color: "#a87c00", tag: "Candidate · review needed" },
  // Path B (inferred from session activity) — candidates land in
  // /admin/knowledge Pending Confirmation, user clicks confirm.
  ltm_promoted:                { color: "#315c2b", tag: "Saved" },
  candidate_created:           { color: "#a87c00", tag: "Candidate · review needed" },
  candidate_bump:              { color: "#7a6200", tag: "Candidate · reinforced" },
  contradiction_detected:      { color: "#c05b00", tag: "Conflict · review needed" },
  contradiction_auto_resolved: { color: "#1a6b3e", tag: "Conflict resolved" },
  contradiction_queued:        { color: "#b00020", tag: "Conflict · review needed" },
  working_memory_written:      { color: "#2a5fa5", tag: "Working memory" },
  memory_superseded:           { color: "#888",    tag: "Superseded" },
};

function fmtTime(ts: number) {
  return new Date(ts).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

function fmtConf(c: number | null) {
  if (c == null) return "";
  return `${Math.round(c * 100)}%`;
}

// ── Single toast ─────────────────────────────────────

function MemoryToast({
  event,
  onDismiss,
}: {
  event: MemoryEvent;
  onDismiss: (id: string) => void;
}) {
  const meta = EVENT_META[event.event_type] ?? { color: "#888", tag: event.event_type };

  return (
    <div className={styles.toast} style={{ borderLeftColor: meta.color }}>
      <div className={styles.toastHeader}>
        <span className={styles.dot} style={{ background: meta.color }} />
        <span className={styles.tag} style={{ color: meta.color }}>
          {meta.tag}
        </span>
        {event.category && (
          <span className={styles.category}>{event.category}</span>
        )}
        {event.confidence != null && (
          <span className={styles.conf}>{fmtConf(event.confidence)}</span>
        )}
        <span className={styles.time}>{fmtTime(event.receivedAt)}</span>
        <button
          className={styles.close}
          onClick={() => onDismiss(event.id)}
          aria-label="Dismiss"
        >
          ×
        </button>
      </div>
      <div className={styles.value}>{event.value}</div>
      {event.detail && (
        <div className={styles.detail}>{event.detail}</div>
      )}
    </div>
  );
}

// ── Log drawer row ────────────────────────────────────

function LogRow({ event }: { event: MemoryEvent }) {
  const meta = EVENT_META[event.event_type] ?? { color: "#888", tag: event.event_type };
  return (
    <div className={styles.logRow}>
      <span className={styles.logDot} style={{ background: meta.color }} />
      <span className={styles.logTime}>{fmtTime(event.receivedAt)}</span>
      <span className={styles.logTag} style={{ color: meta.color }}>
        {meta.tag}
      </span>
      {event.category && (
        <span className={styles.logCat}>[{event.category}]</span>
      )}
      <span className={styles.logValue}>{event.value}</span>
      {event.confidence != null && (
        <span className={styles.logConf}>{fmtConf(event.confidence)}</span>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────

interface MemoryEventFeedProps {
  events: MemoryEvent[];
  allEvents: MemoryEvent[];
  onDismiss: (id: string) => void;
  onDismissAll: () => void;
}

export function MemoryEventFeed({
  events,
  allEvents,
  onDismiss,
  onDismissAll,
}: MemoryEventFeedProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const timers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const visible = events.slice(0, MAX_VISIBLE);

  // Auto-dismiss toasts after 60 s
  useEffect(() => {
    for (const e of visible) {
      if (!timers.current.has(e.id)) {
        const t = setTimeout(() => {
          onDismiss(e.id);
          timers.current.delete(e.id);
        }, AUTO_DISMISS_MS);
        timers.current.set(e.id, t);
      }
    }
    return () => {
      timers.current.forEach((t) => clearTimeout(t));
      timers.current.clear();
    };
  }, [visible, onDismiss]);

  const toggleDrawer = useCallback(() => setDrawerOpen((v) => !v), []);
  const total = allEvents.length;

  return (
    <>
      {/* Toast stack — bottom-right, stacks upward */}
      {visible.length > 0 && (
        <div className={styles.toastStack}>
          {visible.map((e) => (
            <MemoryToast key={e.id} event={e} onDismiss={onDismiss} />
          ))}
        </div>
      )}

      {/* Badge — always visible once any event received */}
      {total > 0 && (
        <button
          className={styles.badge}
          onClick={toggleDrawer}
          title="Memory activity log"
        >
          Mem ({total})
        </button>
      )}

      {/* Log drawer */}
      {drawerOpen && (
        <div className={styles.drawer}>
          <div className={styles.drawerHeader}>
            <span>Memory Activity</span>
            <div className={styles.drawerActions}>
              <button onClick={onDismissAll} className={styles.clearBtn}>
                Clear toasts
              </button>
              <button onClick={toggleDrawer} className={styles.closeDrawer}>
                ×
              </button>
            </div>
          </div>
          <div className={styles.drawerBody}>
            {allEvents.length === 0 ? (
              <div className={styles.empty}>No memory events yet.</div>
            ) : (
              allEvents.map((e) => <LogRow key={e.id} event={e} />)
            )}
          </div>
        </div>
      )}
    </>
  );
}

